import type {
  HashKeyApiResponse,
  ApiKey,
} from "@/features/auth/types/hashKeys.types";
import { Button } from "@/components/ui/button";
import {
  createMonitoringIcon,
  getMonitoringTypeFromToken,
  MONITORING_BADGE_STYLES,
} from "@/constants/monitoring";

interface HashKeyCollapsedRowProps {
  hashKey: HashKeyApiResponse;
  onExpand: () => void;
}

export function HashKeyCollapsedRow({
  hashKey,
  onExpand,
}: HashKeyCollapsedRowProps) {
  return (
    <tr className="bg-black-900/50 hover:bg-black-900/80 border-separate border-b border-neutral-200 transition-colors">
      {/* Hash Key Column */}
      <td className="w-[15%] max-w-[200px] px-4 py-5 text-left">
        <div className="flex min-w-0 flex-col gap-1">
          <div className="truncate text-sm font-bold text-neutral-950">
            {hashKey.hash_key_name}
          </div>
          <div className="max-w-[180px] truncate font-mono text-xs text-neutral-500">
            {hashKey.hash_key_preview}
          </div>
        </div>
      </td>

      {/* API Keys Column */}
      <td className="w-[55%] px-4 py-5 text-left">
        <div className="flex flex-wrap gap-2">
          {hashKey.api_keys.length === 0 ? (
            <span className="text-sm text-neutral-500">No API keys</span>
          ) : (
            hashKey.api_keys.map((apiKey: ApiKey) => {
              const monitoringType = getMonitoringTypeFromToken(
                apiKey.api_key_preview,
              );
              const isPremium = apiKey.api_key_preview
                .toLowerCase()
                .includes("premium");

              const colorClass = isPremium
                ? "bg-yellow-500"
                : MONITORING_BADGE_STYLES[monitoringType] || "bg-gray-500";

              const icon = isPremium ? (
                <i className="ri-star-line text-md text-white" />
              ) : (
                createMonitoringIcon(monitoringType, "text-md", true)
              );

              return (
                <div
                  key={apiKey.api_key_id}
                  className={`flex h-[28px] w-[28px] items-center justify-center rounded-[8px] ${colorClass} p-2 font-medium text-white transition-transform duration-200 hover:scale-105`}
                  title={apiKey.api_key_preview}
                >
                  <div className="flex items-center justify-center text-[20px]">
                    {icon}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </td>

      {/* Status Column */}
      <td className="w-[15%] px-4 py-5 text-left">
        <span
          className={`inline-flex rounded-full px-2 py-1 text-xs font-medium ${
            hashKey.active ? "bg-green-500 text-white" : "bg-red-500 text-white"
          }`}
        >
          {hashKey.active ? "Active" : "Inactive"}
        </span>
      </td>

      {/* Actions Column */}
      <td className="w-[10%] px-4 py-5 text-left">
        <Button
          className="text-muted hover:text-muted gap-2 bg-neutral-100 text-xs leading-normal font-bold tracking-normal hover:bg-neutral-100/80"
          variant="minimal"
          icon
          onClick={onExpand}
        >
          <i className="ri-eye-line text-xs"></i>
          View
        </Button>
      </td>
    </tr>
  );
}
